import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2, Users } from 'lucide-react';
import api from '../api';

const DAY = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const shift = (iso, n) => { const d = new Date(`${iso}T00:00:00`); d.setDate(d.getDate() + n); return d.toISOString().split('T')[0]; };
const md = (iso) => `${iso.slice(5, 7)}/${iso.slice(8, 10)}`;

const WeekNav = ({ data, setWeek }) => (
  <div className="flex items-center gap-1 text-xs text-white/80">
    <button onClick={() => setWeek(shift(data.weekStart, -7))} className="p-1 hover:text-white" data-testid="workload-prev-week"><ChevronLeft className="w-4 h-4" /></button>
    <span data-testid="workload-week-label">{md(data.days[0])} to {md(data.days[6])}</span>
    <button onClick={() => setWeek(shift(data.weekStart, 7))} className="p-1 hover:text-white" data-testid="workload-next-week"><ChevronRight className="w-4 h-4" /></button>
  </div>
);

const Bar = ({ n, max, done }) => (
  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden relative">
    <div className="absolute inset-y-0 left-0 bg-[#d4af37]/50 rounded-full transition-[width] duration-500" style={{ width: `${max ? (n / max) * 100 : 0}%` }} />
    <div className="absolute inset-y-0 left-0 bg-green-600 rounded-full transition-[width] duration-500" style={{ width: `${max ? (done / max) * 100 : 0}%` }} />
  </div>
);

const Grid = ({ data, heavy }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid="workload-grid">
      <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
        <tr><th className="px-3 py-2 text-left">Nurse</th>{data.days.map((d, i) => <th key={d} className="px-2 py-2 text-center">{DAY[i]} {md(d)}</th>)}<th className="px-3 py-2 text-center">Week</th></tr>
      </thead>
      <tbody className="divide-y">
        {data.nurses.map(n => (
          <tr key={n.staffId} data-testid={`workload-row-${n.staffId}`} className={heavy(n) ? 'bg-amber-50' : ''}>
            <td className="px-3 py-2 font-medium text-gray-800 whitespace-nowrap">{n.name}<span className="text-xs text-gray-400 ml-1">{n.role}</span></td>
            {data.days.map(d => {
              const v = n.visits.filter(x => x.date === d);
              return (
                <td key={d} className="px-2 py-2 text-center">
                  {v.length > 0 && (
                    <span title={v.map(x => `${x.patientName} ${x.time}${x.status === 'Completed' ? ' (done)' : ''}`).join('\n')}
                      className={`inline-block min-w-[1.75rem] px-1.5 py-0.5 rounded-full text-xs font-semibold ${v.every(x => x.status === 'Completed') ? 'bg-green-100 text-green-800' : 'bg-gray-900 text-[#d4af37]'}`}>
                      {v.length}
                    </span>
                  )}
                </td>
              );
            })}
            <td className="px-3 py-2 text-center font-semibold" data-testid={`workload-total-${n.staffId}`}>{n.total}{n.completed > 0 && <span className="text-xs text-green-700 font-normal"> ({n.completed} done)</span>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const NurseWorkload = ({ compact = false }) => {
  const [week, setWeek] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = () => api.get(`/recert/workload${week ? `?weekStart=${week}` : ''}`).then(r => setData(r.data)).catch(() => setData({ nurses: [], days: [], weekStart: '' }));
    load();
    window.addEventListener('recert-changed', load);
    return () => window.removeEventListener('recert-changed', load);
  }, [week]);

  const nurses = data?.nurses || [];
  const max = Math.max(0, ...nurses.map(n => n.total));
  const avg = nurses.length ? nurses.reduce((s, n) => s + n.total, 0) / nurses.length : 0;
  const heavy = (n) => n.total >= 2 && n.total > avg + 1;

  return (
    <div className="bg-white rounded border border-gray-200 shadow-sm" data-testid={compact ? 'workload-card' : 'workload-panel'}>
      <div className="flex items-center justify-between px-4 py-2 bg-gray-900 rounded-t">
        <h3 className="font-semibold text-[#d4af37] flex items-center gap-2"><Users className="w-4 h-4" /> Nurse Recert Workload</h3>
        {data?.days?.length > 0 && <WeekNav data={data} setWeek={setWeek} />}
      </div>
      {!data ? <Loader2 className="w-5 h-5 animate-spin m-4" /> : nurses.length === 0 ? (
        <p className="px-4 py-3 text-sm text-gray-500" data-testid="workload-empty">No nurses or recert visits this week.</p>
      ) : compact ? (
        <ul className="divide-y">
          {nurses.map(n => (
            <li key={n.staffId} className={`flex items-center gap-3 px-4 py-2 ${heavy(n) ? 'bg-amber-50' : ''}`} data-testid={`workload-row-${n.staffId}`}>
              <span className="w-40 truncate text-sm font-medium text-gray-800">{n.name}</span>
              <Bar n={n.total} max={max} done={n.completed} />
              <span className="w-20 text-right text-xs text-gray-600" data-testid={`workload-total-${n.staffId}`}>{n.total} visit(s){n.completed ? `, ${n.completed} done` : ''}</span>
            </li>
          ))}
        </ul>
      ) : <Grid data={data} heavy={heavy} />}
      {nurses.some(heavy) && <p className="px-4 py-2 text-xs text-amber-700 border-t" data-testid="workload-heavy-note">Highlighted nurses have more recert visits than the team average this week.</p>}
    </div>
  );
};
